import dynamic from 'next/dynamic';
import Head from 'next/head';
import NextImage from 'next/image';

import React, { useCallback, useEffect, useRef, useState } from 'react';
import ImgsViewer from 'react-images-viewer';
import ReactMarkdown from 'react-markdown';

import rehypeRaw from 'rehype-raw';
import remarkGfm from 'remark-gfm';
import styled from 'styled-components';

import ScrollReveal from '@components/common/ScrollReveal';
import { Container } from '@components/parts/Container';

import { DynamicData } from '@customTypes/DynamicData';
import { Review } from '@customTypes/Review';

const Slider = dynamic(() => import('react-touch-drag-slider'), { ssr: false });

type Props = {
  reviews: Review[];
  dynamicData?: DynamicData;
  useDynamicValue: boolean;
};

const SliderWrapper = styled.div`
  div[data-testid='slider'] {
    width: 306px !important;
    @media (min-width: 992px) {
      width: 366px !important;
    }
  }

  overflow-x: visible;
  height: 490px;
  padding-top: 50px;
  padding-bottom: 90px;
  @media (min-width: 992px) {
    padding-top: 70px;
    padding-bottom: 160px;
    height: 630px;
    margin-left: -12px;
    margin-right: -12px;
  }
  .rtds-slider-styles {
    font: inherit !important;
    word-break: inherit !important;
    .slide-outer {
      touch-action: pan-y !important;
    }
  }
  .rtds-single-slide-styles > div:first-child {
    padding: 0 !important;
  }
`;

const Wrapper = styled.div`
  user-select: none;
  width: 288px;
  height: 350px;
  @media (min-width: 992px) {
    margin-left: 0;
    width: 348px;
    height: 400px;
  }
  display: flex;
  background-color: white;
  border: 1px solid #e3e5ea;
  div.review {
    position: relative;
    width: 100%;
    color: #62656a;
    font-weight: 400;
    display: flex;
    flex-direction: column;
    padding: 24px !important;
    @media (min-width: 992px) {
      padding: 30px !important;
    }
    .user {
      .stars {
        margin-bottom: 0.1em;
      }
      .region {
        color: #a3a7ae;
        font-weight: 500;
        margin-bottom: 1em;
      }
    }
    .description {
      display: -webkit-box;
      overflow: hidden;
      text-overflow: ellipsis;
      -webkit-line-clamp: 6;
      -webkit-box-orient: vertical;
      strong {
        color: black;
      }
    }
    .images {
      position: absolute;
      bottom: 0;
      left: 0;
      right: 0;
      img {
        width: 74px;
        height: 74px;
        margin-right: 5px;
        @media (min-width: 992px) {
          width: 90px;
          height: 90px;
        }
      }
      padding-top: 0 !important;
      padding: 24px;
      @media (min-width: 992px) {
        padding: 30px;
      }
    }
  }
`;

const Star = () => (
  <svg width="21" height="20" viewBox="0 0 21 20" fill="none" xmlns="http://www.w3.org/2000/svg">
    <path
      d="M10.0382 1.11174C10.2089 0.700787 10.7911 0.700788 10.9618 1.11174L13.303 6.74865C13.375 6.92186 13.5378 7.04025 13.7248 7.05526L19.8031 7.54323C20.2462 7.5788 20.426 8.13169 20.0886 8.42113L15.4565 12.3945C15.3144 12.5165 15.2523 12.7076 15.2957 12.8899L16.7107 18.8301C16.8137 19.2628 16.3429 19.6046 15.9634 19.3725L10.7609 16.1904C10.6008 16.0924 10.3992 16.0924 10.2391 16.1904L5.03661 19.3725C4.65714 19.6046 4.18625 19.2628 4.28933 18.8301L5.70431 12.8899C5.74772 12.7076 5.68563 12.5165 5.54346 12.3945L0.911413 8.42113C0.573989 8.13169 0.753809 7.5788 1.19694 7.54323L7.27524 7.05526C7.4622 7.04025 7.62504 6.92186 7.69698 6.74865L10.0382 1.11174Z"
      fill="#FFBF51"
    />
  </svg>
);

const SliderButton = styled.button`
  border: none;
  background: no-repeat url('/svg/review-next.svg');
  cursor: pointer;
  height: 50px;
  width: 50px;
  outline: none;
`;

const Panel = styled.div`
  display: none;
  @media (min-width: 992px) {
    display: flex;
  }
  align-items: center;
  position: absolute;
  top: 70px;
  bottom: 160px;
  right: 0;
  &.left {
    left: -70px;
    button {
      transform: rotate(180deg);
    }
  }
  &.right {
    right: -60px;
  }
  &.hide {
    display: none;
  }
`;

function convertDynamic(dynamicData: DynamicData): Review[] {
  return (
    dynamicData.reviews?.map((review) => ({
      region: review.region,
      content: review.rating_comment,
      images: {
        data: review.thumbnails.map((url) => ({
          attributes: {
            id: Math.random(),
            url: url,
          },
        })),
      },
      rating: review.rating,
    })) ?? []
  );
}

export const Reviews: React.FC<Props> = (props) => {
  const { reviews: $reviews, dynamicData, useDynamicValue } = props;
  const reviews: Review[] = (dynamicData && useDynamicValue ? convertDynamic(dynamicData) : $reviews) ?? [];
  const [currentSlide, _setCurrentSlide] = useState(0);
  const [openedIdx, setOpenedIdx] = useState<number | null>(null);
  const [currentImgIdx, setCurrentImgIdx] = useState(0);
  const timerRef = useRef<any>(null);

  const setCurrentSlide = useCallback(
    (idx: number) => {
      if (idx >= reviews?.length) idx = 0;
      _setCurrentSlide(idx);
    },
    [reviews?.length]
  );

  const next = (multiplier = 1) => {
    const nextIndex = currentSlide + multiplier * 3;
    return setCurrentSlide(Math.min(reviews.length - 3, Math.max(0, nextIndex)));
  };

  useEffect(() => {
    setCurrentSlide(0);
    const doc = document.documentElement;
    doc.style.setProperty('--display-reviews', (reviews?.length ?? 0) < 3 ? 'none' : 'block');
  }, [reviews?.length, setCurrentSlide]);

  return (
    <Container
      className="hide-if-scarce-reviews"
      fullOnMobile
      style={{ backgroundColor: '#F3F4F8' }}
      innerStyle={{ position: 'relative' }}
    >
      <ScrollReveal>
        <Panel className={`left ${currentSlide == 0 ? 'hide' : ''}`}>
          <SliderButton onClick={() => next(-1)} />
        </Panel>
        <Panel className={`right ${currentSlide >= reviews.length - 3 ? 'hide' : ''}`}>
          <SliderButton onClick={() => next(1)} />
        </Panel>
        <SliderWrapper>
          <Slider
            onSlideComplete={(i) => {
              setCurrentSlide(i);
            }}
            onSlideStart={(i) => {
              if (timerRef.current) {
                clearInterval(timerRef.current);
              }
            }}
            activeIndex={currentSlide}
            threshHold={10}
            transition={0.5}
            scaleOnDrag={false}
          >
            {reviews.map((review, idx) => (
              <Wrapper key={idx}>
                <div className="review">
                  <div className="user">
                    <div className="stars">
                      {Array(Math.round(review.rating))
                        .fill(0)
                        .map((r, idx) => (
                          <Star key={idx} />
                        ))}
                    </div>
                    <div className="region">{review.region}</div>
                  </div>
                  <div className="description">
                    <ReactMarkdown remarkPlugins={[remarkGfm]} rehypePlugins={[rehypeRaw]}>
                      {review.content}
                    </ReactMarkdown>
                  </div>
                  {review.images?.data?.length ? (
                    <>
                      <div className="images">
                        {review.images.data.slice(0, 3).map((thumbnail, imageIdx) => (
                          <React.Fragment key={imageIdx}>
                            <Head>
                              <link rel="preload" href={thumbnail.attributes.url} as="image" />
                            </Head>
                            <NextImage
                              onClick={(e) => {
                                e.preventDefault();
                                e.stopPropagation();
                              }}
                              onPointerDown={(e) => {
                                setOpenedIdx(idx);
                                setCurrentImgIdx(imageIdx);
                                e.stopPropagation();
                                e.preventDefault();
                              }}
                              onPointerUp={(e) => {
                                e.stopPropagation();
                                e.preventDefault();
                              }}
                              src={thumbnail.attributes.url}
                              alt="Image"
                              width={92}
                              height={92}
                            />
                          </React.Fragment>
                        ))}
                      </div>
                      <ImgsViewer
                        currImg={currentImgIdx}
                        backdropCloseable
                        imgs={review.images.data.map((image) => ({
                          src: image.attributes.url.replace('/thumbnails/', '/images/'),
                        }))}
                        isOpen={openedIdx === idx}
                        showThumbnails
                        onClickPrev={() => setCurrentImgIdx(Math.max(0, currentImgIdx - 1))}
                        onClickNext={() =>
                          setCurrentImgIdx(Math.min(review.images?.data?.length ?? 1 - 1, currentImgIdx + 1))
                        }
                        onClickThumbnail={(i: number) => setCurrentImgIdx(i)}
                        onClose={() => setOpenedIdx(null)}
                      />
                    </>
                  ) : null}
                </div>
              </Wrapper>
            ))}
          </Slider>
        </SliderWrapper>
      </ScrollReveal>
    </Container>
  );
};
